// The reconnect catch-up of one conversation (#3629 C), moved out of state.svelte.ts:
// one run per conversation at a time, and the read that bridges the hole under its page.
import { workspaceState } from "../../../core/state.svelte.js";
import { olderProof } from "../mirror-reset.js";
import { reportKpi } from "../../../report-failure.js";
import { conversationSync } from "../sync-session.js";
import {
  adoptNewLife,
  agentStreamState,
  catchUpTimelineByDelta,
  cursorEpoch,
  loadRecentTurns,
  oldestSeq,
  onCatchUpTimelineFetchFailed,
  pageBackIfHeadPartial,
  RECENT_PROMPTS,
  reportGapUnreadable,
  sleep,
  staleAfter,
  type TimelineFetchClient,
} from "../state.svelte.js";

// A reconnect / foreground / daemon-online / workspace-select catch-up of one conversation:
// a delta when the stream holds durable rows, else the tail page plus the hole under it.
async function catchUpConversation(
  client: TimelineFetchClient,
  wsId: string,
  agentId: string,
  daemonId: string | undefined,
): Promise<void> {
  // The daemon this catch-up names (the one that came online) routes this and later reads.
  agentStreamState.noteRoute(agentId, daemonId);
  const route = daemonId ?? agentStreamState.routeOf(agentId);
  // The life this read belongs to: a stream cleared or rewound while it is out must
  // not receive its page (#3248 C3).
  const generation = agentStreamState.generationOf(agentId);
  // A turn running when the socket dropped: its reply was cut, and this read is what heals it.
  const cut = agentStreamState.getTurnStatus(agentId) === "running";
  const noteCut = (read: CutRead, outcome: CutOutcome): void => {
    if (cut) reportKpi("agents.stream_cut", { read, outcome, workspaceId: wsId });
  };
  try {
    // Rows after the newest one held (CYBORG-1193); only a stream with no durable seq
    // or an unfinished delta pays the tail page below.
    if (await catchUpTimelineByDelta(client, wsId, agentId, route)) {
      noteCut("delta", "recovered");
      return;
    }
    // Same depth a route open asks for (CYBORG-1180): a reconnect re-hydrates the
    // conversation from scratch, so without this it re-opened on the default tail —
    // one streamed reply's worth of rows — and then had to crawl back for the rest.
    const tail = await client.fetchAgentTimeline(wsId, agentId, {
      lastPrompts: RECENT_PROMPTS,
      ...(route ? { daemonId: route } : {}),
    });
    const { items, hasOlder, olderCursor, maxSeq, source, headPartial, life, recentPrompts } = tail;
    // Read the hole under the page first, or the hydrate replaces the history on screen.
    const gap = await readCatchUpGap(client, wsId, agentId, { items, olderCursor, life }, route);
    if (workspaceState.current?.id !== wsId) return;
    if (agentStreamState.generationOf(agentId) !== generation) return;
    // The session may have been closed/archived while the fetch was in flight —
    // don't resurrect a deleted stream (hydrateFromTimeline would recreate it).
    if (!agentStreamState.streams.has(agentId)) return;
    if (gap.kind === "unreadable") {
      noteCut("tail", "unreadable");
      return;
    }
    if (gap.kind === "life-changed") {
      noteCut("tail", "life_changed");
      void adoptNewLife(client, agentId, route);
      return;
    }
    items.unshift(...gap.rows);
    // The page's pagination meta replaces a stale cursor (which would skip the reconnect gap);
    // maxSeq lets the monotonic guard refuse a lagging page over a fresher live stream.
    const accepted = agentStreamState.hydrateFromTimeline(agentId, items, {
      hasOlder,
      olderProven: olderProof(tail),
      olderCursor,
      maxSeq,
      source,
      life,
    });
    // Recovered once the view holds the page's top row of a proven life, replaced or merged.
    const held = agentStreamState.highestHeldSeq(agentId) ?? 0;
    const reached = maxSeq === undefined || held >= maxSeq;
    const proven = !agentStreamState.isLifeUnproven(agentId);
    noteCut("tail", reached && proven ? "recovered" : "behind");
    // A page that opens inside a reply heals the same way it does on a route open.
    pageBackIfHeadPartial(
      client,
      agentId,
      { items, hasOlder, headPartial, olderCursor },
      accepted,
      route,
    );
    // A reload pages back to a readable depth; a reconnect must too. A server that
    // answered `lastPrompts` already sent them — only an older one pages back.
    if (accepted && recentPrompts === undefined) {
      void loadRecentTurns(client, agentId, route);
    }
  } catch (err) {
    // intentional: onCatchUpTimelineFetchFailed marks a refusal and reports anything else;
    // a rejection from the previous life marks nothing in this one (#3248 E2).
    if (agentStreamState.generationOf(agentId) !== generation) return;
    noteCut("none", "failed");
    // Refused → marked, so no later reconnect asks again. Anything else → reported.
    // Neither toasts: nobody opened this stream (onCatchUpTimelineFetchFailed).
    onCatchUpTimelineFetchFailed(agentId, wsId, err);
  }
}

type CutRead = "delta" | "tail" | "none";
type CutOutcome = "recovered" | "behind" | "unreadable" | "life_changed" | "failed";

interface CatchUpDemand {
  client: TimelineFetchClient;
  wsId: string;
  daemonId: string | undefined;
  // The conversation's generation when the demand was made: a clear or rewind since
  // makes it another conversation's, and it is not run.
  generation: number;
}

interface CatchUpRun {
  // The latest trigger that arrived while a run was out: it runs once more, after.
  again?: CatchUpDemand;
  done: Promise<void>;
}

const catchUpRuns = conversationSync.collection("catchUpRuns", new Map<string, CatchUpRun>());

// One catch-up per conversation at a time (#3382, M2). A trigger during a run is not
// dropped: the run goes once more with the latest arguments, and every caller waits for it.
export function catchUpAgent(
  client: TimelineFetchClient,
  wsId: string,
  agentId: string,
  daemonId?: string,
): Promise<void> {
  const demand: CatchUpDemand = {
    client,
    wsId,
    daemonId,
    generation: agentStreamState.generationOf(agentId),
  };
  const running = catchUpRuns.get(agentId);
  if (running) {
    running.again = demand;
    return running.done;
  }
  const run: CatchUpRun = { done: Promise.resolve() };
  catchUpRuns.set(agentId, run);
  run.done = (async () => {
    let next: CatchUpDemand | undefined = demand;
    try {
      while (next) {
        run.again = undefined;
        // Each demand runs with its own client and only in the generation it was made in.
        if (agentStreamState.generationOf(agentId) === next.generation) {
          await catchUpConversation(next.client, next.wsId, agentId, next.daemonId);
        }
        next = run.again;
      }
    } finally {
      // In the same step as the last check: a trigger after it starts a new run, and no
      // trigger can land on a run that has already decided to end.
      if (catchUpRuns.get(agentId) === run) catchUpRuns.delete(agentId);
    }
  })();
  return run.done;
}

// A reconnect page that starts above the highest row on screen leaves a hole: read it before
// the hydrate, so the page lands contiguous. A wider hole is not read here: the hydrate keeps
// the screen under it and the bounded read-back fills it after the tail is shown (#3338).
export const CATCH_UP_GAP_MAX_ROWS = 2_000;
// Rows per bridge read: a smaller frame keeps the socket's pong from queueing behind it.
export const CATCH_UP_GAP_PAGE_ROWS = 100;
const CATCH_UP_GAP_RETRIES = 3;

export type CatchUpGap =
  | { kind: "rows"; rows: Record<string, unknown>[] }
  // A page of another life (a rewind elsewhere, mid-bridge): the caller adopts it whole.
  | { kind: "life-changed" }
  // A hole the reads could not fill, or not prove of this life: the view stays.
  | { kind: "unreadable" };

export async function readCatchUpGap(
  client: TimelineFetchClient,
  wsId: string,
  agentId: string,
  page: { items: Record<string, unknown>[]; olderCursor: string | null; life?: string },
  daemonId?: string,
  wait: (ms: number) => Promise<void> = sleep,
): Promise<CatchUpGap> {
  const none = { kind: "rows" as const, rows: [] };
  const held = agentStreamState.highestHeldSeq(agentId);
  const floor = oldestSeq(page.items);
  if (held === undefined || floor === undefined) return none;
  const missing = floor - held - 1;
  if (missing <= 0 || missing > CATCH_UP_GAP_MAX_ROWS) return none;
  // Not a life marker (a daemon restart changes a live page's), so only its format counts.
  const epoch = cursorEpoch(page.olderCursor);
  if (epoch === undefined) return none;
  const stale = staleAfter(agentId, wsId);
  const rows: Record<string, unknown>[] = [];
  let cursor = floor;
  let failures = 0;
  while (cursor - held > 1) {
    // The caller drops a stale result; stop paying reads for it.
    if (stale()) return { kind: "unreadable" };
    let problem: unknown;
    try {
      const older = await client.fetchAgentTimeline(wsId, agentId, {
        cursor: JSON.stringify({ epoch, seq: cursor }),
        direction: "older",
        limit: Math.min(cursor - held - 1, CATCH_UP_GAP_PAGE_ROWS),
        ...(daemonId ? { daemonId } : {}),
      });
      // Every bridge page must be the reconnect page's own life, or the two are not one.
      if (page.life !== undefined && older.life !== page.life) {
        if (older.life !== undefined) return { kind: "life-changed" };
        reportGapUnreadable(agentId, held, cursor, new Error("bridge page names no life"));
        return { kind: "unreadable" };
      }
      const oldest = older.source === "blind" ? undefined : oldestSeq(older.items);
      if (oldest !== undefined && oldest < cursor) {
        rows.unshift(...older.items);
        cursor = oldest;
        failures = 0;
        continue;
      }
      problem = new Error(
        `page made no progress below seq ${cursor} (${older.source ?? "daemon"})`,
      );
    } catch (err) {
      // intentional: kept as the problem the bounded retry reports (reportGapUnreadable)
      problem = err;
    }
    failures += 1;
    if (failures > CATCH_UP_GAP_RETRIES) {
      reportGapUnreadable(agentId, held, cursor, problem);
      return { kind: "unreadable" };
    }
    await wait(500 * 2 ** (failures - 1));
  }
  return { kind: "rows", rows };
}
